import { isEqual, omit } from "lodash-es";
import { computed, type MaybeRefOrGetter, ref, toValue, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { type Validations } from "shared/composables/useValidation";
import DateRange from "shared/helpers/DateRange";
import {
  convertMentionTypesToSocialTypes,
  convertSocialTypesToMentionTypes,
  getMediaFromTypes,
  getTypesForMedia,
  getTypesForStreams,
  influencerSocial,
  social,
} from "shared/helpers/media";
import { isSocialStream } from "shared/helpers/stream";
import { useChartInterval } from "shared/modules/Charts/composables/useChartInterval";
import {
  CHARTS_WITH_INSTAGRAM_HANDLE_SELECTION,
  CHARTS_WITH_SOCIAL_ENGAGE_INSIGHTS_METRICS,
  DEFAULT_INITIAL_INSTAGRAM_ACCOUNT_SELECTION,
  DEFAULT_MAX_SELECTED_INSTAGRAM_ACCOUNTS,
  DEFAULT_MAX_SELECTED_STREAMS,
  DEFAULT_MIN_SELECTED_STREAMS,
  DEFAULT_OUTREACH_INSIGHTS_METRICS,
  DEFAULT_OUTREACH_PERFORMANCE_METRICS,
  DEFAULT_SOCIAL_ENGAGE_PERFORMANCE_METRICS,
  DEFAULT_SOCIAL_INSIGHTS_METRICS,
  DEFAULT_STREAM_INSIGHTS_METRICS,
} from "shared/modules/Charts/constants";
import { normalizeChartSetting } from "shared/modules/Charts/helpers/charts";
import {
  type BackendChartConfig,
  type BaseChartSetting,
  type ChartIndex,
  type ChartSetting,
  type ChartSettingWithStreamsAndMediaTypes,
  type ChartsWithSocialEngageInsightsMetrics,
  getOutreachActivityChartFilters,
} from "shared/modules/Charts/types";
import {
  DEFAULT_SOV_METRIC_TYPE,
  DEFAULT_TOP_ARTICLES_METRIC,
} from "shared/modules/Charts/types/chartSettings";
import { type Chart } from "shared/modules/Charts/types/chartTypes";
import {
  hasIntentionalEmptyMentionStreams,
  isChartSettingWithAIVisibility,
  isChartSettingWithAnalyticsIntegration,
  isChartSettingWithContactKeys,
  isChartSettingWithExportOptions,
  isChartSettingWithFixedPlatform,
  isChartSettingWithInstagramHandleSelection,
  isChartSettingWithKeyMessages,
  isChartSettingWithLinkedSocialStreams,
  isChartSettingWithMediaReleases,
  isChartSettingWithMentionStreamsAndSocialStreams,
  isChartSettingWithOptionalMentionStreams,
  isChartSettingWithOutreachActivityFilters,
  isChartSettingWithOutreachInsightsMetrics,
  isChartSettingWithOutreachPerformanceMetrics,
  isChartSettingWithPostSelection,
  isChartSettingWithSocialAccountSelection,
  isChartSettingWithSocialEngagePerformanceMetrics,
  isChartSettingWithSocialInsightsMetrics,
  isChartSettingWithSocialStreamsOnly,
  isChartSettingWithSortBy,
  isChartSettingWithSovMetricType,
  isChartSettingWithStreamInsightsMetrics,
  isChartSettingWithStreamsAndMediaTypes,
} from "shared/modules/Charts/types/guards";
import {
  getChartSettingWithClearedMentionStreamDependencies,
  getChartSettingWithMovedMentionStreamDependencies,
} from "shared/modules/InsightsChart/helpers/insightsChartStreamSettingHelper";
import { useInstantInsightsCharts } from "shared/modules/InstantInsights/composables/useInstantInsightsCharts";
import features from "shared/services/features";
import useStreamsStore from "shared/stores/streams";
import { type Nullable, type Stream } from "shared/types";
import {
  type MentionType,
  type SocialMentionType,
} from "shared/types/mentions";
import type { SocialAccountOption } from "shared/types/social";

interface StreamPickerValidations {
  maxSelected: number;
  validations: Validations;
}

export function useInsightsChart(options: {
  chartSetting: MaybeRefOrGetter<Nullable<ChartSetting>>;
  defaultChartSettings: MaybeRefOrGetter<ChartIndex>;
  defaultRange: MaybeRefOrGetter<DateRange>;
  defaultStreamIds?: MaybeRefOrGetter<number[]>;
  instagramHandleOptions?: MaybeRefOrGetter<SocialAccountOption[]>;
  ownedInstagramHandleOptions?: MaybeRefOrGetter<SocialAccountOption[]>;
  sharedStreams?: MaybeRefOrGetter<Stream[]>;
  socialAccountOptions?: MaybeRefOrGetter<SocialAccountOption[]>;
  socialAnalyseChart?: MaybeRefOrGetter<boolean>;
}) {
  const {
    chartSetting,
    defaultChartSettings,
    defaultRange,
    defaultStreamIds,
    instagramHandleOptions: instagramHandleOptionsProp,
    ownedInstagramHandleOptions: ownedInstagramHandleOptionsProp,
    sharedStreams,
    socialAccountOptions: socialAccountOptionsProp,
    socialAnalyseChart,
  } = options;

  const socialAccountOptions = computed<SocialAccountOption[]>(
    () => toValue(socialAccountOptionsProp) ?? []
  );

  const instagramHandleOptions = computed<SocialAccountOption[]>(
    () => toValue(instagramHandleOptionsProp) ?? []
  );

  const ownedInstagramHandleOptions = computed<SocialAccountOption[]>(
    () => toValue(ownedInstagramHandleOptionsProp) ?? []
  );

  const { chartsIndex } = useInstantInsightsCharts();

  const { canUseChartInterval, getAllowedInterval } = useChartInterval();

  const { getStreamById } = useStreamsStore();

  const formData = ref<ChartSetting>(initFormData());

  function resolveStreams(streamIds: number[]): Stream[] {
    return streamIds
      .map((id) => {
        const sharedStream = toValue(sharedStreams)?.find(
          (stream) => stream.id === id
        );

        if (sharedStream) {
          return sharedStream;
        }

        return getStreamById(id);
      })
      .filter((stream): stream is Stream => Boolean(stream));
  }

  function isSocialType(type: MentionType): boolean {
    return social.relatedFields.includes(type);
  }

  const allStreams = computed<Stream[]>(() => {
    if (!isChartSettingWithStreamsAndMediaTypes(formData.value)) {
      return [];
    }

    return resolveStreams(formData.value.stream_ids ?? []);
  });

  const allSelectedStreams = computed({
    get() {
      // Return stream_ids directly from formData to avoid reactivity issues
      // when background API calls update the streams store
      if (!isChartSettingWithStreamsAndMediaTypes(formData.value)) {
        return [];
      }

      return (
        (formData.value as ChartSettingWithStreamsAndMediaTypes).stream_ids ??
        []
      );
    },
    set(value) {
      if (!isChartSettingWithStreamsAndMediaTypes(formData.value)) {
        return;
      }

      (formData.value as ChartSettingWithStreamsAndMediaTypes).stream_ids =
        value;
    },
  });

  const socialStreams = computed<Stream[]>(() =>
    allStreams.value.filter((stream) => isSocialStream(stream))
  );

  const hasSocialStreams = computed<boolean>(
    () => socialStreams.value.length > 0
  );

  const selectedSocialStreams = computed<Stream["id"][]>({
    get() {
      return socialStreams.value.map((stream) => stream.id);
    },
    set(value) {
      if (!isChartSettingWithStreamsAndMediaTypes(formData.value)) {
        return;
      }

      if (
        isChartSettingWithSocialStreamsOnly(formData.value) ||
        toValue(socialAnalyseChart)
      ) {
        formData.value.stream_ids = value;

        return;
      }

      if (isChartSettingWithMentionStreamsAndSocialStreams(formData.value)) {
        (formData.value as ChartSettingWithStreamsAndMediaTypes).stream_ids = [
          ...value,
          // preserve mention streams
          ...selectedMentionStreams.value,
        ];
      }
    },
  });

  const mentionStreams = computed<Stream[]>(() => {
    const socialStreamIds = socialStreams.value.map((stream) => stream.id);

    return allStreams.value.filter(
      (stream) => !socialStreamIds.includes(stream.id)
    );
  });

  const hasMentionStreams = computed<boolean>(
    () => mentionStreams.value.length > 0
  );

  const selectedMentionStreams = computed<Stream["id"][]>({
    get() {
      return mentionStreams.value.map((stream) => stream.id);
    },
    set(value) {
      if (
        !isChartSettingWithStreamsAndMediaTypes(formData.value) ||
        isChartSettingWithSocialStreamsOnly(formData.value) ||
        toValue(socialAnalyseChart)
      ) {
        return;
      }

      if (isChartSettingWithMentionStreamsAndSocialStreams(formData.value)) {
        (formData.value as ChartSettingWithStreamsAndMediaTypes).stream_ids = [
          ...value,
          // preserve social streams
          ...selectedSocialStreams.value,
        ];

        return;
      }

      (formData.value as ChartSettingWithStreamsAndMediaTypes).stream_ids =
        value;
    },
  });

  const allTypes = computed<MentionType[]>(() => {
    if (!isChartSettingWithStreamsAndMediaTypes(formData.value)) {
      return [];
    }

    return [
      ...((formData.value as ChartSettingWithStreamsAndMediaTypes).types ?? []),
    ];
  });

  const availableMentionTypes = computed<MentionType[]>(() =>
    getTypesForStreams(mentionStreams.value)
  );

  const availableSocialTypes = computed<MentionType[]>(() =>
    getTypesForStreams(socialStreams.value)
  );

  const selectedSocialTypes = computed<SocialMentionType[]>({
    get() {
      if (
        !isChartSettingWithStreamsAndMediaTypes(formData.value) ||
        !hasSocialStreams.value
      ) {
        return [];
      }

      const types = allTypes.value.filter((type) =>
        availableSocialTypes.value.includes(type)
      );

      return convertMentionTypesToSocialTypes(types).sort();
    },
    set(value) {
      if (!isChartSettingWithStreamsAndMediaTypes(formData.value)) {
        return;
      }

      const updatedTypes: MentionType[] =
        convertSocialTypesToMentionTypes(value);

      if (isChartSettingWithMentionStreamsAndSocialStreams(formData.value)) {
        formData.value.types = [
          // preserve mention types
          ...selectedMentionTypes.value,
          ...updatedTypes,
        ];

        return;
      }

      if (
        isChartSettingWithSocialStreamsOnly(formData.value) ||
        toValue(socialAnalyseChart)
      ) {
        (formData.value as ChartSettingWithStreamsAndMediaTypes).types =
          updatedTypes;
      }
    },
  });

  const selectedMentionTypes = computed<MentionType[]>({
    get() {
      if (
        !isChartSettingWithStreamsAndMediaTypes(formData.value) ||
        !hasMentionStreams.value
      ) {
        return [];
      }

      const socialFieldsToExclude = features.has(influencerSocial.featureFlag)
        ? [] // Allow all social types if influencer social is enabled
        : social.relatedFields; // Exclude social types if influencer social is disabled

      return allTypes.value.filter(
        (type) =>
          availableMentionTypes.value.includes(type) &&
          !socialFieldsToExclude.includes(type)
      );
    },
    set(value) {
      if (
        !isChartSettingWithStreamsAndMediaTypes(formData.value) ||
        isChartSettingWithSocialStreamsOnly(formData.value) ||
        toValue(socialAnalyseChart)
      ) {
        return;
      }

      if (isChartSettingWithMentionStreamsAndSocialStreams(formData.value)) {
        // preserve social types
        (formData.value as ChartSettingWithStreamsAndMediaTypes).types = [
          ...value,
          ...convertSocialTypesToMentionTypes(selectedSocialTypes.value),
        ];

        return;
      }

      (formData.value as ChartSettingWithStreamsAndMediaTypes).types = value;
    },
  });

  const selectedPostIds = computed<number[]>({
    get() {
      if (isChartSettingWithPostSelection(formData.value)) {
        return formData.value.post_ids ?? [];
      }

      return [];
    },
    set(value) {
      if (isChartSettingWithPostSelection(formData.value)) {
        formData.value.post_ids = value;
      }
    },
  });

  const selectedRange = computed<DateRange>({
    get() {
      if (formData.value.range?.range?.label) {
        return DateRange.rangeForLabel(formData.value.range.range?.label);
      }

      if (
        !formData.value.range ||
        formData.value.range.before === undefined ||
        formData.value.range.after === undefined
      ) {
        return DateRange.lastThirtyDays();
      }

      return new DateRange({
        after: formData.value.range.after,
        before: formData.value.range.before,
      });
    },
    set(value) {
      if (value) {
        formData.value.range = value;
      }
    },
  });

  const maxSelectedStreams = computed<number>(() => {
    const chartConfig = chartsIndex.value[formData.value.chart!];

    return chartConfig?.maxStreams ?? DEFAULT_MAX_SELECTED_STREAMS;
  });

  const streamPickerValidations = computed<StreamPickerValidations>(() => {
    if (!isChartSettingWithStreamsAndMediaTypes(formData.value)) {
      return {
        maxSelected: 0,
        validations: [],
      };
    }

    const currentTotalCount: number = formData.value.stream_ids?.length ?? 0;

    const validations: Validations = [
      () =>
        currentTotalCount >= DEFAULT_MIN_SELECTED_STREAMS ||
        getLocaleText("instant_insights_add_chart_form.select_min_streams", {
          count: DEFAULT_MIN_SELECTED_STREAMS,
        }),
    ];

    return {
      maxSelected: maxSelectedStreams.value,
      validations,
    };
  });

  const defaultStreams = computed<Stream[]>(() => {
    let streamIds: number[] = [];

    const defaultStreamIdsValue = toValue(defaultStreamIds);

    if (defaultStreamIdsValue) {
      streamIds = defaultStreamIdsValue;
    } else if (isChartSettingWithStreamsAndMediaTypes(formData.value)) {
      streamIds = formData.value.stream_ids ?? [];
    }

    return streamIds
      .map((id) => getStreamById(id))
      .filter((stream): stream is Stream => Boolean(stream));
  });

  function getStreamTypeGroups(streams: Stream[]): {
    mentionStreams: Stream[];
    mentionTypes: MentionType[];
    socialStreams: Stream[];
    socialTypes: MentionType[];
  } {
    const groupedMentionStreams = streams.filter(
      (stream) => !isSocialStream(stream)
    );

    const groupedSocialStreams = streams.filter((stream) =>
      isSocialStream(stream)
    );

    const mentionTypes = getTypesForStreams(groupedMentionStreams).filter(
      (type) =>
        features.has(influencerSocial.featureFlag) || !isSocialType(type)
    );

    const socialTypes = getTypesForStreams(groupedSocialStreams);

    return {
      mentionStreams: groupedMentionStreams,
      mentionTypes,
      socialStreams: groupedSocialStreams,
      socialTypes,
    };
  }

  const canHandleSocialStreams = computed<boolean>(
    () =>
      isChartSettingWithSocialStreamsOnly(formData.value) ||
      isChartSettingWithMentionStreamsAndSocialStreams(formData.value) ||
      Boolean(toValue(socialAnalyseChart))
  );

  const canHandleMentionStreams = computed<boolean>(
    () =>
      !isChartSettingWithSocialStreamsOnly(formData.value) &&
      !toValue(socialAnalyseChart)
  );

  const hasDefaultChartRange = computed<boolean>(() => {
    const chartSettingValue = toValue(chartSetting);

    if (!chartSettingValue) {
      return false;
    }

    const defaultRangeValue = toValue(defaultRange);

    return (
      chartSettingValue.range?.before === defaultRangeValue.before &&
      chartSettingValue.range?.after === defaultRangeValue.after
    );
  });

  const hasDefaultTypes = computed<boolean>(() => {
    const chartSettingValue = toValue(chartSetting);

    if (!chartSettingValue) {
      return false;
    }

    if (!isChartSettingWithStreamsAndMediaTypes(chartSettingValue)) {
      return true;
    }

    const { types } = getDefaultStreamsAndTypes(chartSettingValue);

    return isEqual(
      [...types].sort(),
      [...(chartSettingValue.types ?? [])].sort()
    );
  });

  const hasDefaultInterval = computed<boolean>(() => {
    const chartSettingValue = toValue(chartSetting);

    if (!chartSettingValue) {
      return false;
    }

    if (!canUseChartInterval(chartSettingValue) || !chartSettingValue.range) {
      return true;
    }

    const defaultInterval = getAllowedInterval(chartSettingValue.range);

    return defaultInterval === chartSettingValue.interval;
  });

  const hasAdvancedFilters = computed<boolean>(() => {
    const chartSettingValue = toValue(chartSetting);

    if (!chartSettingValue) {
      return false;
    }

    return Boolean(chartSettingValue.advanced_filters);
  });

  function getDefaultChartSetting(chart?: Chart): Nullable<BackendChartConfig> {
    return chart ? toValue(defaultChartSettings)[chart] : null;
  }

  function getDefaultSocialAccountIds(
    chart: Chart,
    accountOptions: SocialAccountOption[],
    ownedAccountOptions: SocialAccountOption[] = []
  ): string[] {
    const accountIds = accountOptions
      .map((integration) => integration.value)
      .filter((id): id is string => id !== undefined);

    const maxSelectedAccounts = CHARTS_WITH_INSTAGRAM_HANDLE_SELECTION.some(
      (instagramChart) => instagramChart === chart
    )
      ? DEFAULT_MAX_SELECTED_INSTAGRAM_ACCOUNTS
      : undefined;

    if (chart === "instagram_public_and_owned_account_followers_benchmark") {
      const ownedAccountIds = ownedAccountOptions
        .map((integration) => integration.value)
        .filter((id): id is string => id !== undefined);

      return [
        ...accountIds.slice(0, DEFAULT_INITIAL_INSTAGRAM_ACCOUNT_SELECTION),
        ...ownedAccountIds.slice(
          0,
          DEFAULT_INITIAL_INSTAGRAM_ACCOUNT_SELECTION
        ),
      ];
    }

    if (chart === "instagram_public_account_follower_growth") {
      return accountIds.slice(0, DEFAULT_INITIAL_INSTAGRAM_ACCOUNT_SELECTION);
    }

    return maxSelectedAccounts
      ? accountIds.slice(0, maxSelectedAccounts)
      : accountIds;
  }

  function getDefaultStreamsAndTypes(item: ChartSetting): {
    streamIds: number[];
    types: MentionType[];
  } {
    const normalizedItem = normalizeChartSetting(item);

    if (!isChartSettingWithStreamsAndMediaTypes(normalizedItem)) {
      return {
        streamIds: [],
        types: [],
      };
    }

    const {
      mentionStreams: defaultMentionStreams,
      mentionTypes,
      socialStreams: defaultSocialStreams,
      socialTypes,
    } = getStreamTypeGroups(defaultStreams.value);

    const socialStreamIds: number[] = defaultSocialStreams.map(
      (stream) => stream.id
    );

    if (toValue(socialAnalyseChart)) {
      return {
        streamIds: socialStreamIds,
        // Fixed-platform types are already normalized above; non-fixed-platform
        // charts use the stream-derived social defaults.
        types: isChartSettingWithFixedPlatform(normalizedItem)
          ? normalizedItem.types
          : socialTypes,
      };
    }

    if (isChartSettingWithSocialStreamsOnly(normalizedItem)) {
      return {
        streamIds: socialStreamIds,
        // Fixed-platform types are already normalized above; non-fixed-platform
        // charts use the stream-derived social defaults so hasDefaultTypes
        // correctly detects user customizations.
        types: isChartSettingWithFixedPlatform(normalizedItem)
          ? normalizedItem.types
          : socialTypes,
      };
    }

    const mentionStreamIds: number[] = defaultMentionStreams.map(
      (stream) => stream.id
    );

    if (isChartSettingWithMentionStreamsAndSocialStreams(normalizedItem)) {
      return {
        streamIds: defaultStreams.value.map((stream) => stream.id),
        types: [...mentionTypes, ...socialTypes],
      };
    }

    return {
      streamIds: mentionStreamIds,
      types: mentionTypes,
    };
  }

  function getUpdatedChartSetting(item: ChartSetting): ChartSetting {
    const normalizedItem = normalizeChartSetting(item);

    if (
      isChartSettingWithOutreachPerformanceMetrics(normalizedItem) &&
      !normalizedItem.insights_metrics?.length
    ) {
      normalizedItem.insights_metrics = DEFAULT_OUTREACH_PERFORMANCE_METRICS;
    }

    if (!isChartSettingWithStreamsAndMediaTypes(normalizedItem)) {
      return normalizedItem;
    }

    const updatedItem = hasIntentionalEmptyMentionStreams(normalizedItem)
      ? getChartSettingWithClearedMentionStreamDependencies(normalizedItem)
      : normalizedItem;

    const currentChartTypes = updatedItem.types ?? [];
    const streamIds = updatedItem.stream_ids;

    if (!streamIds) {
      return updatedItem;
    }

    const selectedStreams = resolveStreams(streamIds);

    if (streamIds.length > 0 && selectedStreams.length === 0) {
      return updatedItem;
    }

    const {
      mentionTypes: nextMentionTypes,
      socialTypes: nextAvailableSocialTypes,
    } = getStreamTypeGroups(selectedStreams);

    const currentMentionChartTypes = currentChartTypes.filter(
      (type) => nextMentionTypes.includes(type) || !isSocialType(type)
    );

    const previousMentionMedia = getMediaFromTypes(currentMentionChartTypes);

    const nextMentionTypesForSelectedMedia = getTypesForMedia(
      previousMentionMedia
    ).filter((type) => nextMentionTypes.includes(type));

    const nextSocialTypes = currentChartTypes.filter(
      (type) => isSocialType(type) && nextAvailableSocialTypes.includes(type)
    );

    return {
      ...updatedItem,
      types: [
        ...nextMentionTypesForSelectedMedia,
        ...nextSocialTypes,
      ].toSorted(),
    };
  }

  function clearTypes(isSocial?: boolean): void {
    if (isSocial) {
      selectedSocialTypes.value = [];
    } else {
      selectedMentionTypes.value = [];
    }
  }

  function autoSelectAllTypes(isSocial?: boolean): void {
    if (isSocial) {
      selectedSocialTypes.value = convertMentionTypesToSocialTypes(
        availableSocialTypes.value
      );
    } else {
      selectedMentionTypes.value = availableMentionTypes.value;
    }
  }

  function getAvailableTypesForStreamChange(
    streams: Stream[],
    isSocial?: boolean
  ): MentionType[] | SocialMentionType[] {
    const { mentionTypes, socialTypes } = getStreamTypeGroups(streams);

    return isSocial
      ? convertMentionTypesToSocialTypes(socialTypes)
      : mentionTypes;
  }

  function handleStreamChange({
    isSocial = false,
    newStreams,
    oldStreams,
  }: {
    isSocial?: boolean;
    newStreams: Stream[];
    oldStreams?: Stream[];
  }): void {
    if (!isChartSettingWithStreamsAndMediaTypes(formData.value)) {
      return;
    }

    // Skip if chart type doesn't handle this stream type
    if (isSocial && !canHandleSocialStreams.value) {
      return;
    }

    if (!isSocial && !canHandleMentionStreams.value) {
      return;
    }

    const streamsWereCleared =
      newStreams.length === 0 && (oldStreams?.length ?? 0) > 0;

    const streamsWereChanged = !isEqual(
      newStreams.map(({ id }) => id).sort(),
      (oldStreams ?? []).map(({ id }) => id).sort()
    );

    if (
      !isSocial &&
      streamsWereChanged &&
      newStreams.length === 1 &&
      oldStreams?.length === 1
    ) {
      const oldStreamId = oldStreams[0].id;
      const newStreamId = newStreams[0].id;

      formData.value = getChartSettingWithMovedMentionStreamDependencies(
        formData.value,
        oldStreamId,
        newStreamId
      );
    }

    const availableTypesWereChanged = !isEqual(
      [...getAvailableTypesForStreamChange(newStreams, isSocial)].sort(),
      [...getAvailableTypesForStreamChange(oldStreams ?? [], isSocial)].sort()
    );

    let availableTypesSelected: MentionType[] | SocialMentionType[] =
      selectedMentionTypes.value.filter((type) =>
        availableMentionTypes.value.includes(type)
      );

    if (isSocial) {
      availableTypesSelected = selectedSocialTypes.value.filter((type) =>
        convertMentionTypesToSocialTypes(availableSocialTypes.value).includes(
          type
        )
      );
    }

    const noAvailableTypesSelected = availableTypesSelected.length === 0;

    if (streamsWereCleared) {
      clearTypes(isSocial);
    } else if (streamsWereChanged && noAvailableTypesSelected) {
      autoSelectAllTypes(isSocial);
    } else if (availableTypesWereChanged) {
      formData.value = getUpdatedChartSetting(formData.value);
    }

    // Migrate key message queries when mention streams change
    if (
      !isSocial &&
      streamsWereChanged &&
      isChartSettingWithKeyMessages(formData.value) &&
      newStreams.length > 0 &&
      formData.value.queries?.length
    ) {
      const targetStreamId = newStreams[0].id;

      formData.value.queries = formData.value.queries.map((query) => ({
        ...query,
        stream_id: targetStreamId,
      }));
    }
  }

  function clearMentionStreams(): void {
    if (!isChartSettingWithOptionalMentionStreams(formData.value)) {
      return;
    }

    formData.value = getChartSettingWithClearedMentionStreamDependencies(
      formData.value
    );

    selectedMentionStreams.value = [];
  }

  function getInitialChartSetting(item: ChartSetting): ChartSetting {
    const defaultSetting = getDefaultChartSetting(item.chart);

    const initialChartSetting = {
      ...omit(
        {
          ...defaultSetting,
          ...item,
        },
        ["label", "info_tooltip", "chart_variants", "default_chart_variant"]
      ),
    } satisfies BaseChartSetting;

    initialChartSetting.range =
      toValue(defaultRange) ?? DateRange.lastThirtyDays();

    if (isChartSettingWithStreamsAndMediaTypes(initialChartSetting)) {
      const { streamIds, types } =
        getDefaultStreamsAndTypes(initialChartSetting);

      initialChartSetting.stream_ids = streamIds;
      initialChartSetting.types = types;
    }

    if (isChartSettingWithMediaReleases(initialChartSetting)) {
      initialChartSetting.media_release_ids = [];
    }

    if (isChartSettingWithOutreachActivityFilters(initialChartSetting)) {
      initialChartSetting.outreach_activity_filters =
        getOutreachActivityChartFilters(
          initialChartSetting.outreach_activity_filters
        );
    }

    if (isChartSettingWithKeyMessages(initialChartSetting)) {
      initialChartSetting.queries = [];
    }

    if (isChartSettingWithLinkedSocialStreams(initialChartSetting)) {
      initialChartSetting.linked_stream_ids = {};
    }

    if (
      defaultSetting &&
      isChartSettingWithExportOptions(initialChartSetting) &&
      isChartSettingWithExportOptions(defaultSetting)
    ) {
      initialChartSetting.export_options = defaultSetting.export_options;
    }

    if (isChartSettingWithStreamInsightsMetrics(initialChartSetting)) {
      initialChartSetting.insights_metrics = DEFAULT_STREAM_INSIGHTS_METRICS;
    }

    if (isChartSettingWithOutreachInsightsMetrics(initialChartSetting)) {
      initialChartSetting.insights_metrics = DEFAULT_OUTREACH_INSIGHTS_METRICS;
    }

    if (isChartSettingWithOutreachPerformanceMetrics(initialChartSetting)) {
      initialChartSetting.insights_metrics =
        DEFAULT_OUTREACH_PERFORMANCE_METRICS;
    }

    if (isChartSettingWithSocialInsightsMetrics(initialChartSetting)) {
      initialChartSetting.insights_metrics = DEFAULT_SOCIAL_INSIGHTS_METRICS;
    }

    if (
      isChartSettingWithSocialEngagePerformanceMetrics(initialChartSetting) &&
      CHARTS_WITH_SOCIAL_ENGAGE_INSIGHTS_METRICS.includes(
        initialChartSetting.chart as ChartsWithSocialEngageInsightsMetrics
      )
    ) {
      initialChartSetting.insights_metrics =
        DEFAULT_SOCIAL_ENGAGE_PERFORMANCE_METRICS;
    }

    if (
      isChartSettingWithSocialAccountSelection(initialChartSetting) ||
      isChartSettingWithInstagramHandleSelection(initialChartSetting) ||
      isChartSettingWithPostSelection(initialChartSetting)
    ) {
      // Set all social integrations as default when the chart needs account
      // IDs for account or post selection.
      if (
        isChartSettingWithInstagramHandleSelection(initialChartSetting) &&
        !initialChartSetting
          .instagram_handles_organisation_social_integration_ids?.length
      ) {
        initialChartSetting.instagram_handles_organisation_social_integration_ids =
          getDefaultSocialAccountIds(
            initialChartSetting.chart,
            instagramHandleOptions.value,
            ownedInstagramHandleOptions.value
          );
      } else if (
        !isChartSettingWithInstagramHandleSelection(initialChartSetting) &&
        !initialChartSetting.organisation_social_integration_ids?.length
      ) {
        const accountIds = getDefaultSocialAccountIds(
          initialChartSetting.chart,
          socialAccountOptions.value
        );

        initialChartSetting.organisation_social_integration_ids =
          initialChartSetting.chart === "net_follower_growth"
            ? accountIds.slice(0, 1)
            : accountIds;
      }
    }

    if (isChartSettingWithAnalyticsIntegration(initialChartSetting)) {
      initialChartSetting.analytics_integration_target = undefined;
    }

    if (isChartSettingWithSovMetricType(initialChartSetting)) {
      initialChartSetting.metric_type = DEFAULT_SOV_METRIC_TYPE;
    }

    if (isChartSettingWithSortBy(initialChartSetting)) {
      initialChartSetting.sort_by = DEFAULT_TOP_ARTICLES_METRIC;
    }

    if (isChartSettingWithAIVisibility(initialChartSetting)) {
      initialChartSetting.llm_entities = [];
      initialChartSetting.llm_models = [];
    }

    if (isChartSettingWithContactKeys(initialChartSetting)) {
      initialChartSetting.show_contacts_only = false;
    }

    return normalizeChartSetting({
      ...item,
      ...initialChartSetting,
    });
  }

  function initFormData(chart?: Chart, isChartChange?: boolean): ChartSetting {
    const defaultSetting = getDefaultChartSetting(chart);
    const chartSettingValue = toValue(chartSetting);

    let chartVariant = chartSettingValue?.chart_variant;

    if (isChartChange || !chartVariant) {
      chartVariant = defaultSetting?.default_chart_variant;
    }

    const baseData: ChartSetting = {
      chart,
      info_tooltip: "",
      label: "",
    };

    if (chartVariant) {
      baseData.chart_variant = chartVariant;
    }

    if (chartSettingValue && !isChartChange) {
      return { ...baseData, ...getUpdatedChartSetting(chartSettingValue) };
    }

    if (!chart || !defaultSetting) {
      return baseData;
    }

    return getInitialChartSetting(baseData);
  }

  function ensureDefaultSocialAccounts(): void {
    if (
      !isChartSettingWithSocialAccountSelection(formData.value) &&
      !isChartSettingWithInstagramHandleSelection(formData.value) &&
      !isChartSettingWithPostSelection(formData.value)
    ) {
      return;
    }

    if (
      formData.value.chart === "net_follower_growth" &&
      formData.value.organisation_social_integration_ids?.length
    ) {
      formData.value.organisation_social_integration_ids =
        formData.value.organisation_social_integration_ids.slice(0, 1);

      return;
    }

    if (
      isChartSettingWithInstagramHandleSelection(formData.value)
        ? formData.value.instagram_handles_organisation_social_integration_ids
            ?.length
        : formData.value.organisation_social_integration_ids?.length
    ) {
      return;
    }

    const accountOptions = isChartSettingWithInstagramHandleSelection(
      formData.value
    )
      ? instagramHandleOptions.value
      : socialAccountOptions.value;

    const ownedAccountOptions = isChartSettingWithInstagramHandleSelection(
      formData.value
    )
      ? ownedInstagramHandleOptions.value
      : [];

    if (!accountOptions.length && !ownedAccountOptions.length) {
      return;
    }

    const selectedAccountIds = getDefaultSocialAccountIds(
      formData.value.chart,
      accountOptions,
      ownedAccountOptions
    );

    if (isChartSettingWithInstagramHandleSelection(formData.value)) {
      formData.value.instagram_handles_organisation_social_integration_ids =
        selectedAccountIds;
    } else {
      formData.value.organisation_social_integration_ids =
        formData.value.chart === "net_follower_growth"
          ? selectedAccountIds.slice(0, 1)
          : selectedAccountIds;
    }
  }

  watch(
    () => formData.value.chart,
    (chart, previousChart) => {
      const isInitialChartLoad =
        previousChart === undefined && chart === toValue(chartSetting)?.chart;

      const isChartChange =
        chart !== previousChart && !isInitialChartLoad && chart !== undefined;

      formData.value = initFormData(chart, isChartChange);
    },
    { immediate: true }
  );

  watch(
    () => toValue(chartSetting),
    (chartSettingValue) => {
      if (isEqual(chartSettingValue, formData.value)) {
        return;
      }

      formData.value = initFormData(chartSettingValue?.chart);
    },
    { deep: true }
  );

  watch(
    () => [
      socialAccountOptions.value,
      instagramHandleOptions.value,
      formData.value.chart,
    ],
    () => {
      ensureDefaultSocialAccounts();
    },
    { deep: true, immediate: true }
  );

  watch(mentionStreams, (newStreams, oldStreams) => {
    handleStreamChange({
      isSocial: false,
      newStreams,
      oldStreams,
    });
  });

  watch(socialStreams, (newStreams, oldStreams) => {
    handleStreamChange({
      isSocial: true,
      newStreams,
      oldStreams,
    });
  });

  return {
    allSelectedStreams,
    allStreams,
    allTypes,
    availableMentionTypes,
    availableSocialTypes,
    clearMentionStreams,
    formData,
    getDefaultSocialAccountIds,
    getDefaultStreamsAndTypes,
    getInitialChartSetting,
    hasAdvancedFilters,
    hasDefaultChartRange,
    hasDefaultInterval,
    hasDefaultTypes,
    hasMentionStreams,
    hasSocialStreams,
    mentionStreams,
    selectedMentionStreams,
    selectedMentionTypes,
    selectedPostIds,
    selectedRange,
    selectedSocialStreams,
    selectedSocialTypes,

    socialStreams,
    streamPickerValidations,
  };
}

export default useInsightsChart;
